import axios from "axios";
import qs from "qs";
import AuthenticationResponse from "../api/models/authenticationResult";
import { appInsights } from "../../config/analytics/AppInsights";
import CONSTANTS from "./constants";
import History from "../../config/history";

let currentRequest = null;
let tokenRefreshTimerid = null;
let reAuthCb = null;
let randomTime = null;

const signoutAndRedirectToLogin = () => {
  SignOut();
  randomTime = null;

  History.replace({
    pathname: "/login",
  });
}
const storageUpdate = (resolver) => {
  var handler = (event) => {
    if (event.key === 'authenticationToken') {
      if (event.newValue !== null) {
        const token = JSON.parse(event.newValue);
        if (token && token.AccessToken) {
          resolver();
        }
      }
    }
    window.removeEventListener("storage", handler);
  }
  return handler;
}

const waitForStorageEvent = async (timeout) => {
  let promiseArray = [
    new Promise(resolve => window.addEventListener("storage", storageUpdate(resolve))),
    new Promise(resolve => setTimeout(resolve, timeout))
  ];

  await Promise.race(promiseArray);
}

window.addEventListener('storage', function (event) {
  // If authenticationToken is null'd by some other browser window, we'll get a notification here
  if (event.key === 'authenticationToken') {
    if (event.newValue === null) {
      signoutAndRedirectToLogin();
    } else {
      const token = JSON.parse(event.newValue);
      if (token && token.AccessToken) {
        if (reAuthCb) {
          reAuthCb(token.AccessToken);
        }
      }
    }
  }
});

const getAuthResp = () => {
  const storedToken = localStorage.getItem('authenticationToken');
  if (storedToken && storedToken !== 'null') {
    const tokenObject = JSON.parse(storedToken);

    const authData = {
      access_token: tokenObject.AccessToken,
      refresh_token: tokenObject.RefreshToken,
      issued_at: tokenObject.IssuedAt,
      expires_in: (new Date(tokenObject.ExpiryDate).getTime() - new Date(tokenObject.IssuedAt).getTime()) / 1000,

      force_password_change: tokenObject.ChangePassword,
      region: tokenObject.region,
      error: tokenObject.ErrorType,
      error_description: tokenObject.ErrorMessage,
      success: tokenObject.Success ? true : false,
    }

    return new AuthenticationResponse(authData.success, tokenObject.GrantType, authData);
  } else {
    return null;
  }
}

const setAuthResp = (token) => {
  if (token) {
    localStorage.setItem('authenticationToken', JSON.stringify(token));
  } else {
    localStorage.removeItem('authenticationToken', null);
  }
}

const isValidToken = () => {
  const authResponse = getAuthResp();
  if (validAccessToken(authResponse) || validRefreshToken(authResponse)) {
    return true;
  }
  return false;
}

const RequestPasswordReset = async (username, emailAddress) => {
  const data = {
    username: username,
    email: emailAddress,
    grant_type: CONSTANTS.AUTH_CONSTANTS.GRANT_TYPE.PASSWORD_RESET,
    client_id: CONSTANTS.AUTH_CONSTANTS.CLIENT_ID,
  };

  let result = await AuthenticationBase(data);
  setAuthResp(result);
  return result;
};

const ValidateResetCode = async (username, resetCode) => {
  const data = {
    username: username,
    reset_code: resetCode,
    grant_type: CONSTANTS.AUTH_CONSTANTS.GRANT_TYPE.PASSWORD_RESET_CODE,
    client_id: CONSTANTS.AUTH_CONSTANTS.CLIENT_ID,
  };

  let result = await AuthenticationBase(data);
  setAuthResp(result);
  return result;
};

const Authenticate = async (credentials) => {
  const data = {
    client_id: CONSTANTS.AUTH_CONSTANTS.CLIENT_ID,
    grant_type: CONSTANTS.AUTH_CONSTANTS.GRANT_TYPE.PASSWORD,
    username: credentials.username,
    password: credentials.password,
  };

  let result = await AuthenticationBase(data);
  setAuthResp(result);

  if (result.Success) {
    localStorage.setItem('username', credentials.username);
    appInsights.setAuthenticatedUserContext(credentials.username, null, true);
  } else {
    localStorage.removeItem('username');
    appInsights.clearAuthenticatedUserContext();
  }

  return result;
};

const SignOut = () => {
  localStorage.removeItem('username');
  appInsights.clearAuthenticatedUserContext();
  appInsights.flush();

  // Do not continue updating access token
  clearTimeout(tokenRefreshTimerid);
  setAuthResp(null);
};

const RefreshToken = async () => {
  const authResponse = getAuthResp();
  if (!validRefreshToken(authResponse)) {
    // no stored auth response or refresh token or refresh token has expired
    // always return a promise so that you can chain results.
    setAuthResp(null);

    return Promise.reject(
      new AuthenticationResponse(false, CONSTANTS.AUTH_CONSTANTS.GRANT_TYPE.REFRESH, {
        error: "INVALID_REFRESH_TOKEN",
        error_description: "No Valid Authentication Token"
      })
    );
  }

  const data = {
    client_id: CONSTANTS.AUTH_CONSTANTS.CLIENT_ID,
    grant_type: CONSTANTS.AUTH_CONSTANTS.GRANT_TYPE.REFRESH,
    refresh_token: authResponse.RefreshToken,
  };

  let result = await AuthenticationBase(data);
  return result;
};

const startRefreshTimer = (authResp) => {
  if (authResp.GrantType === CONSTANTS.AUTH_CONSTANTS.GRANT_TYPE.PASSWORD ||
    authResp.GrantType === CONSTANTS.AUTH_CONSTANTS.GRANT_TYPE.REFRESH) {

    // Create token refresh timer to automatically refresh the access token.
    let tokenExpiryTime = new Date(authResp.ExpiryDate).getTime() - new Date().getTime();

    randomTime = createRandomTimeOffset(authResp);
    tokenExpiryTime -= randomTime;

    clearTimeout(tokenRefreshTimerid);
    tokenRefreshTimerid = setTimeout(refreshTokenTimerFn, tokenExpiryTime);
  }
}

const createRandomTimeOffset = (authResp) => {
  // Default to 4.5 secs as a maximum, as the validity functions use 5 seconds as check
  let maxRandomTimeMs = 4500;

  if (authResp && authResp.ExpiryDate && authResp.IssuedAt) {
    let expiresIn = authResp.ExpiryDate.getTime() - authResp.IssuedAt.getTime();

    // Maximum of 10% of expiry time is used
    maxRandomTimeMs = (expiresIn * 0.1);
  }
  return Math.floor(Math.random() * maxRandomTimeMs);
}

const AuthenticationBase = async (data) => {
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();

  setTimeout(() => source.cancel(), 11000);

  const headers = {};
  headers[CONSTANTS.HTTP_HEADER.FIELD.CONTENT_TYPE] =
    CONSTANTS.HTTP_HEADER.CONTENT_TYPE_VALUES.FORM_URL_ENCODED;

  const config = {
    baseURL: CONSTANTS.AUTH_CONSTANTS.AUTH_BASE_URL,
    headers: headers,
    timeout: 10000,
    cancelToken: source.token,
  };

  try {
    const response = await axios.post(
      CONSTANTS.AUTH_CONSTANTS.AUTH_ENDPOINT,
      qs.stringify(data),
      config
    );

    let result = new AuthenticationResponse(true, data.grant_type, response.data);

    if (result.Success) {
      if (!response.data.refresh_token) {
        appInsights.trackEvent({ name: "Auth response is missing a refresh token" });
      }

      // Subtract a random amount of time from the expiry time. This is a max of 10% of the total time.
      // Default to 4.5 seconds otherwise
      randomTime = createRandomTimeOffset(result);

      startRefreshTimer(result);
    } else {
      setAuthResp(null);
    }
    return result;
  } catch (error) {
    appInsights.trackException({ exception: error, properties: { function: "AuthenticationBase", cookie: document.cookie } });

    if (error.response) {
      if (error.response.data.error === "invalid_grant") {
        // If we got here, its possible that we had two tabs/windows logged in at the same time.
        // Scenario is:
        //  Tab1 logs in and gets a token that needs a refresh in at time T
        //    Starts a timer to fire at time T
        //  Tab2 starts up and finds an existing token in local storage that expires at time T
        //    Starts a timer to fire at time T
        //
        // Both timers fire at time T (often just a few ms apart)
        // Both make a web request using the same refresh token R (gained from local storage)
        // One of the refresh requests works - i.e. API returns valid updated credentials
        // The other refresh request fails with a 400 Inavlid Grant
        //    - API logs show that the Refresh Token R was used but is no longer valid.

        // Wait for an update to the auth token in local storage. Wait up to 5 seconds
        await waitForStorageEvent(5000);

        // Get the token from local storage again, and check to see if it is now valid.
        // If it is, then the other tab updated successfully. If it isn't then this 400 Invalid grant  
        // is probably some other auth failure.
        let latesttoken = getAuthResp();
        if (validAccessToken(latesttoken)) {
          // Found a token from somewhere else! reusing it
          startRefreshTimer(latesttoken);

          return latesttoken;
        }
      }

      appInsights.trackException({ exception: error, properties: { function: "AuthenticationBase", cookie: document.cookie } });

      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      setAuthResp(null);
      return new AuthenticationResponse(false, data.grant_type, error.response.data);
    }

    // generic error
    setAuthResp(null);
    return new AuthenticationResponse(false, data.grant_type, {
      error: "SYSTEM",
      error_description: error.message,
    });
  }
};

const validRefreshToken = (token) => token && token.isRefreshTokenValid();

const validAccessToken = (token) => token && token.isValid();

const createNewRefreshRequest = (authResponse) => {
  // if we have a valid refresh token then we should use it to authenticate
  if (validRefreshToken(authResponse)) {
    currentRequest = RefreshToken();
  } else {
    // there is no valid stored refresh token - error
    throw new Error("Failed because credentials are blank");
  }
}

const getToken = () => {
  const authResponse = getAuthResp();
  if (validAccessToken(authResponse) &&
      !authResponse.needsRefresh(randomTime)) {
    // We have a valid token

    // a. If there is no existing refresh timer running (tokenRefreshTimerid is null/undefined), This is probably the first time getToken has been called.

    // b. If this is launched by refreshTokenTimer due to token.isValid saying a refresh is due, but by the time we
    //    get here the refresh is no longer needed (i.e. another tab updated it), we still need to reorganise the 
    //    timer to refresh at the right time.

    // So in both these cases, restart the timer

    startRefreshTimer(authResponse);
    return Promise.resolve(authResponse.AccessToken);
  }

  // we do not have a valid stored token
  if (!currentRequest) {
    // there is no currently pending auth request
    createNewRefreshRequest(authResponse);
  }

  // the token has expired or doesn't exist - request a new one
  return currentRequest
    .then((response) => {
      // store the current authentication response for future use
      setAuthResp(response);
      if (response && response.AccessToken) {
        // return a resolved promise with the token
        startRefreshTimer(response);

        if (reAuthCb) {
          reAuthCb(response.AccessToken);
        }

        return Promise.resolve(response.AccessToken);
      } else {
        if (response.ErrorType) {
          throw new Error(
            "Failed to get authentication token: " + response.ErrorType
          );
        }
        else {
          throw new Error("Failed to get authentication token");
        }
      }
    })
    .finally(() => {
      currentRequest = null;
    });
};

const setReAuthCallback = (cb) => {
  reAuthCb = cb;
}

const refreshTokenTimerFn = async () => {
  let response = null;

  clearTimeout(tokenRefreshTimerid);

  try {
    response = await getToken();
  } catch (err) {
    console.warn("Error refreshing token - ", err);

    appInsights.trackException({ exception: err, properties: { function: "refreshTokenTimerFn", cookie: document.cookie } });

    // kick user out of app here
    signoutAndRedirectToLogin();
  }

  return response;
}

export default {
  Authenticate,
  SignOut,
  RequestPasswordReset,
  ValidateResetCode,
  getToken,
  setReAuthCallback,
  isValidToken,
  signoutAndRedirectToLogin,
};


